import { useAppStore } from '@/state-management/store';
import { useShallow } from 'zustand/react/shallow';
import type { PrescriptionAssistantSliceType } from '../slices/prescriptionAssistant/types';

/**
 * Zustand selector to get the medication pad items from the prescription assistant state.
 * @param state - PrescriptionAssistantSliceType
 * @returns MedicationPadItemType[]
 */
export const medicationPadItemsSelector = (state: PrescriptionAssistantSliceType) =>
  state.prescriptionAssistant.prescriptionPad.medicationPadItems;

/**
 * Direct Zustand selector hook to get the medication pad items.
 * This is a more performant way to access the medication pad items directly from the store.
 * It avoids unnecessary re-renders by using shallow comparison.
 * @example
 * const medicationPadItems = useMedicationPadItemsSelector();
 * @see medicationPadItemsSelector for the selector function.
 * @see useAppStore for the Zustand store.
 * @see PrescriptionAssistantSliceType for the state type.
 * @see MedicationPadItemType for the item type.
 * @returns MedicationPadItemType[]
 */
export const useMedicationPadItemsSelector = () => useAppStore(useShallow(medicationPadItemsSelector));

/**
 * Zustand selector hook to get a specific medication pad item by its ID.
 * This is useful for accessing a single item without needing to filter the entire list.
 * @param medicationPadItemId - The ID of the medication pad item to retrieve.
 * @returns MedicationPadItemType
 */
export const useMedicationPadItemSelector = (medicationPadItemId: string) =>
  useAppStore(
    useShallow(
      (state) =>
        medicationPadItemsSelector(state).find((item) => item.medicationPadItemId === medicationPadItemId) ?? {
          medicationPadItemId,
        },
    ),
  );
